import { POLL_MS } from '../lib/polling';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { PageHeader, Tabs } from '../components/Layout';
import {
  ConfirmDialog,
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  TextInput,
  useToast,
} from '../components/ui';

/**
 * Phase 7 centralized device management (docs/android/PHASE-7-
 * ARCHITECTURE.md, DEVICE-MANAGEMENT.md). The Phase 6 "POS terminals" tab
 * on the Users & Roles screen (pages/Admin.tsx) still exists unchanged -
 * this is the fuller fleet view the brief asks for: search/filter/
 * pagination, per-device configuration/commands/health/updates/audit, and
 * the bulk-unsafe actions (disable, decommission, publish config, issue a
 * command) all behind an explicit confirmation.
 */

type DeviceStatus = 'UNREGISTERED' | 'REGISTERED' | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'DECOMMISSIONED';
type DeviceHealth = 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'OFFLINE';

interface DeviceSummary {
  id: string;
  deviceId: string;
  deviceCode: string;
  name: string | null;
  terminalNumber: string | null;
  status: DeviceStatus;
  deviceType: string;
  rolloutGroup: 'PILOT' | 'PRODUCTION';
  registered: boolean;
  appVersion: string | null;
  androidVersion: string | null;
  lastSeenAt: string;
  lastHeartbeatAt: string | null;
  lastSyncAt: string | null;
  pendingSyncCount: number | null;
  failedSyncCount: number | null;
  lastSyncError: string | null;
  batteryPercent: number | null;
  batteryCharging: boolean | null;
  storageTotalMb: number | null;
  storageAvailableMb: number | null;
  kioskActive: boolean | null;
  appliedConfigVersion: number;
  dbSchemaVersion: number | null;
  health: DeviceHealth;
}

interface DeviceListResponse {
  items: DeviceSummary[];
  total: number;
  page: number;
  pageSize: number;
}

interface DashboardSummary {
  total: number;
  online: number;
  offline: number;
  healthy: number;
  warning: number;
  critical: number;
  syncFailures: number;
}

const HEALTH_TONE: Record<DeviceHealth, 'ok' | 'warn' | 'danger' | ''> = {
  HEALTHY: 'ok',
  WARNING: 'warn',
  CRITICAL: 'danger',
  OFFLINE: '',
};

const STATUS_TONE: Record<DeviceStatus, 'ok' | 'warn' | 'danger' | ''> = {
  UNREGISTERED: '',
  REGISTERED: '',
  ACTIVE: 'ok',
  INACTIVE: '',
  SUSPENDED: 'warn',
  DECOMMISSIONED: 'danger',
};

function Chip({ label, tone }: { label: string; tone: 'ok' | 'warn' | 'danger' | '' }) {
  return <span className={`chip ${tone}`}>{label}</span>;
}

export function DeviceManagementPage() {
  const queryClient = useQueryClient();
  const { fail } = useToast();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [health, setHealth] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [selected, setSelected] = useState<string | null>(null);
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string } | null>(null);
  const [showPairingForm, setShowPairingForm] = useState(false);

  const summary = useQuery({
    queryKey: ['device-summary'],
    queryFn: () => api.get<DashboardSummary>('/api/devices/summary'),
    refetchInterval: POLL_MS,
  });

  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  if (health) params.set('health', health);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  const list = useQuery({
    queryKey: ['devices', q, status, health, page],
    queryFn: () => api.get<DeviceListResponse>(`/api/devices?${params.toString()}`),
    refetchInterval: POLL_MS,
  });

  const createPairingCode = useMutation({
    mutationFn: (expiresInMinutes: number) =>
      api.post<{ code: string; expiresAt: string }>('/api/devices/pairing-codes', { expiresInMinutes }),
    onSuccess: (data) => {
      setPairing(data);
      setShowPairingForm(false);
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: fail,
  });

  const refresh = () => {
    void list.refetch();
    void summary.refetch();
  };

  return (
    <div className="stack">
      <PageHeader
        title="Device Management"
        subtitle="Your POS fleet: registration, configuration, health, updates and audit"
        actions={
          <button className="btn primary" onClick={() => setShowPairingForm(true)}>
            + Pair a device
          </button>
        }
      />

      <div className="kpis">
        <Kpi label="Devices" value={summary.data?.total ?? '—'} />
        <Kpi label="Online" value={summary.data?.online ?? '—'} tone="blue" />
        <Kpi label="Offline" value={summary.data?.offline ?? '—'} />
        <Kpi
          label="Warning"
          value={summary.data?.warning ?? '—'}
          tone={summary.data?.warning ? 'amber' : ''}
        />
        <Kpi
          label="Critical"
          value={summary.data?.critical ?? '—'}
          tone={summary.data?.critical ? 'red' : ''}
        />
        <Kpi
          label="Sync failures"
          value={summary.data?.syncFailures ?? '—'}
          tone={summary.data?.syncFailures ? 'amber' : ''}
        />
      </div>

      <div className="card">
        <div className="card-head row gap-6 wrap">
          <input
            className="input"
            placeholder="Search device, code, terminal, version…"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            style={{ maxWidth: 280 }}
          />
          <select
            className="select"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Any status</option>
            {(['UNREGISTERED', 'REGISTERED', 'ACTIVE', 'INACTIVE', 'SUSPENDED', 'DECOMMISSIONED'] as const).map(
              (s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ),
            )}
          </select>
          <select
            className="select"
            value={health}
            onChange={(e) => {
              setPage(1);
              setHealth(e.target.value);
            }}
          >
            <option value="">Any health</option>
            {(['HEALTHY', 'WARNING', 'CRITICAL', 'OFFLINE'] as const).map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
          <button className="btn sm" onClick={refresh}>
            Refresh
          </button>
        </div>

        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <Empty message="No device matches these filters" />
        ) : (
          <>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Location / Terminal</th>
                    <th>Status</th>
                    <th>App version</th>
                    <th>Last seen</th>
                    <th>Last sync</th>
                    <th className="num">Pending sync</th>
                    <th>Health</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data?.items.map((d) => (
                    <tr key={d.deviceId} className="clickable" onClick={() => setSelected(d.deviceId)}>
                      <td>
                        <div className="b">{d.name ?? '(unnamed)'}</div>
                        <div className="xs muted mono">{d.deviceCode}</div>
                      </td>
                      <td className="small">{d.terminalNumber ?? '—'}</td>
                      <td>
                        <Chip label={d.status} tone={STATUS_TONE[d.status]} />
                      </td>
                      <td className="small mono">{d.appVersion ?? '—'}</td>
                      <td className="small">{dateTime(d.lastSeenAt)}</td>
                      <td className="small">{d.lastSyncAt ? dateTime(d.lastSyncAt) : '—'}</td>
                      <td className="num">{d.pendingSyncCount ?? '—'}</td>
                      <td>
                        <Chip label={d.health} tone={HEALTH_TONE[d.health]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-body row gap-6 between">
              <span className="small muted">
                {list.data?.total ?? 0} device(s) - page {page}
              </span>
              <div className="row gap-6">
                <button className="btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </button>
                <button
                  className="btn sm"
                  disabled={(list.data?.items.length ?? 0) < pageSize}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showPairingForm ? (
        <PairingForm
          onCancel={() => setShowPairingForm(false)}
          onSubmit={(minutes) => createPairingCode.mutate(minutes)}
          busy={createPairingCode.isPending}
        />
      ) : null}

      {pairing ? <PairingCodeModal pairing={pairing} onClose={() => setPairing(null)} /> : null}

      {selected ? (
        <DeviceDetailDrawer
          deviceId={selected}
          onClose={() => {
            setSelected(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function PairingForm({
  onCancel,
  onSubmit,
  busy,
}: {
  onCancel: () => void;
  onSubmit: (minutes: number) => void;
  busy: boolean;
}) {
  const [minutes, setMinutes] = useState(15);
  return (
    <Modal
      title="Pair a new device"
      size="narrow"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => onSubmit(minutes)} disabled={busy}>
            {busy ? 'Generating…' : 'Generate code'}
          </button>
        </>
      }
    >
      <p className="small muted">
        This code is shown once, expires after the time you choose, and can be used to pair exactly
        one tablet. Enter it on the tablet's pairing screen before it expires.
      </p>
      <TextInput
        label="Expires in (minutes)"
        type="number"
        min={1}
        max={60}
        value={minutes}
        onChange={(e) => setMinutes(Number(e.target.value) || 15)}
      />
    </Modal>
  );
}

function PairingCodeModal({
  pairing,
  onClose,
}: {
  pairing: { code: string; expiresAt: string };
  onClose: () => void;
}) {
  return (
    <Modal title="Pairing code" size="narrow" onClose={onClose} footer={<button className="btn primary" onClick={onClose}>Done</button>}>
      <div className="stack" style={{ alignItems: 'center', textAlign: 'center' }}>
        <div className="mono" style={{ fontSize: 32, letterSpacing: 4 }}>
          {pairing.code}
        </div>
        <p className="small muted">Expires {dateTime(pairing.expiresAt)}. Single use.</p>
      </div>
    </Modal>
  );
}

type DetailTab = 'overview' | 'configuration' | 'health' | 'commands' | 'updates' | 'audit';

function DeviceDetailDrawer({ deviceId, onClose }: { deviceId: string; onClose: () => void }) {
  const [tab, setTab] = useState<DetailTab>('overview');
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<null | { kind: 'disable' | 'decommission' | 'enable' | 'wipe' }>(
    null,
  );

  const detail = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => api.get<DeviceSummary>(`/api/devices/${deviceId}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['devices'] });
    void queryClient.invalidateQueries({ queryKey: ['device-summary'] });
  };

  const disable = useMutation({
    mutationFn: (reason: string) => api.post(`/api/devices/${deviceId}/disable`, { reason }),
    onSuccess: () => {
      push('Device disabled. It will be refused the moment it next reaches the server.');
      invalidate();
    },
    onError: fail,
  });
  const enable = useMutation({
    mutationFn: () => api.post(`/api/devices/${deviceId}/enable`, {}),
    onSuccess: () => {
      push('Device re-enabled');
      invalidate();
    },
    onError: fail,
  });
  const decommission = useMutation({
    mutationFn: (reason: string) => api.post(`/api/devices/${deviceId}/decommission`, { reason }),
    onSuccess: () => {
      push('Device decommissioned. Its credential has been revoked.');
      invalidate();
    },
    onError: fail,
  });
  const syncNow = useMutation({
    mutationFn: () => api.post(`/api/devices/${deviceId}/commands`, { type: 'SYNC_NOW' }),
    onSuccess: () => {
      push('Sync Now queued - the device will act on it the next time it checks in.');
      void queryClient.invalidateQueries({ queryKey: ['device-commands', deviceId] });
    },
    onError: fail,
  });
  const wipe = useMutation({
    // A day to be picked up: a till that is switched off overnight still erases itself next morning.
    mutationFn: () =>
      api.post(`/api/devices/${deviceId}/commands`, { type: 'WIPE_LOCAL_DATA', expiresInMinutes: 1440 }),
    onSuccess: () => {
      push('Erase queued - the device erases its local data the next time it checks in.');
      void queryClient.invalidateQueries({ queryKey: ['device-commands', deviceId] });
    },
    onError: fail,
  });
  const refreshConfig = useMutation({
    mutationFn: () => api.post(`/api/devices/${deviceId}/commands`, { type: 'REFRESH_CONFIGURATION' }),
    onSuccess: () => {
      push('Refresh Configuration queued');
      void queryClient.invalidateQueries({ queryKey: ['device-commands', deviceId] });
    },
    onError: fail,
  });

  if (detail.isLoading) {
    return (
      <Drawer title="Device" onClose={onClose}>
        <Loading />
      </Drawer>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <Drawer title="Device" onClose={onClose}>
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      </Drawer>
    );
  }

  const d = detail.data;
  const canDisable = d.status !== 'DECOMMISSIONED';
  const canEnable = d.status === 'SUSPENDED' || d.status === 'INACTIVE';
  const canDecommission = d.status !== 'DECOMMISSIONED';

  return (
    <Drawer
      title={d.name ?? d.deviceCode}
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn sm" onClick={() => syncNow.mutate()} disabled={syncNow.isPending}>
            Sync Now
          </button>
          <button className="btn sm" onClick={() => refreshConfig.mutate()} disabled={refreshConfig.isPending}>
            Refresh Configuration
          </button>
          {d.status !== 'DECOMMISSIONED' ? (
            <button className="btn sm danger" onClick={() => setConfirmAction({ kind: 'wipe' })} disabled={wipe.isPending}>
              Erase local data
            </button>
          ) : null}
          {canEnable ? (
            <button className="btn sm" onClick={() => setConfirmAction({ kind: 'enable' })}>
              Enable
            </button>
          ) : null}
          {canDisable ? (
            <button
              className="btn sm danger"
              onClick={() => setConfirmAction({ kind: 'disable' })}
            >
              Disable
            </button>
          ) : null}
          {canDecommission ? (
            <button
              className="btn sm danger"
              onClick={() => setConfirmAction({ kind: 'decommission' })}
            >
              Decommission
            </button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        <div className="row gap-6">
          <Chip label={d.status} tone={STATUS_TONE[d.status]} />
          <Chip label={d.health} tone={HEALTH_TONE[d.health]} />
          <span className="xs muted mono">{d.deviceId}</span>
        </div>

        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'configuration', label: 'Configuration' },
            { id: 'health', label: 'Health & Sync' },
            { id: 'commands', label: 'Commands' },
            { id: 'updates', label: 'Updates' },
            { id: 'audit', label: 'Audit' },
          ]}
          active={tab}
          onChange={setTab}
        />

        {tab === 'overview' ? <OverviewTab d={d} onSaved={invalidate} /> : null}
        {tab === 'configuration' ? <ConfigurationTab deviceId={deviceId} appliedVersion={d.appliedConfigVersion} /> : null}
        {tab === 'health' ? <HealthTab d={d} /> : null}
        {tab === 'commands' ? <CommandsTab deviceId={deviceId} /> : null}
        {tab === 'updates' ? <UpdatesTab deviceId={deviceId} /> : null}
        {tab === 'audit' ? <AuditTab deviceId={deviceId} /> : null}
      </div>

      {confirmAction?.kind === 'enable' ? (
        <ConfirmDialog
          title="Re-enable this device?"
          message="It will be able to authenticate and operate normally again."
          confirmLabel="Enable"
          busy={enable.isPending}
          onConfirm={() => {
            enable.mutate();
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      ) : null}
      {confirmAction?.kind === 'disable' ? (
        <ReasonConfirm
          title="Disable this device?"
          message="It will be refused the next time it reaches the server (an offline device cannot be reached immediately). This does not delete anything it has already synced."
          confirmLabel="Disable"
          busy={disable.isPending}
          onConfirm={(reason) => {
            disable.mutate(reason);
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      ) : null}
      {confirmAction?.kind === 'wipe' ? (
        <ConfirmDialog
          title="Erase this device's local data?"
          message="The next time it checks in, the device erases its offline database (bills, orders, staff list, offline sign-in), its credential and its sign-ins, and returns to the pairing screen. Anything it has not yet sent to the server is lost - check its pending count first. A device that is decommissioned erases itself the same way."
          confirmLabel="Erase"
          busy={wipe.isPending}
          onConfirm={() => {
            wipe.mutate();
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      ) : null}
      {confirmAction?.kind === 'decommission' ? (
        <ReasonConfirm
          title="Decommission this device?"
          message="This is terminal: its credential is revoked immediately and it can never be re-enabled. The next time it reaches the server it erases its local data, including anything not yet synced. Historical transactions it already synced are never deleted. Pair a replacement instead."
          confirmLabel="Decommission"
          busy={decommission.isPending}
          onConfirm={(reason) => {
            decommission.mutate(reason);
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      ) : null}
    </Drawer>
  );
}

function ReasonConfirm({
  title,
  message,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      title={title}
      size="narrow"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn danger"
            disabled={busy || reason.trim().length === 0}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="small muted">{message}</p>
      <TextInput
        label="Reason (required, recorded in the audit log)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. lost tablet, being replaced, maintenance"
      />
    </Modal>
  );
}

function FieldGrid({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {rows.map(([label, value]) => (
        <div className="row between" key={label}>
          <span className="small muted">{label}</span>
          <span className="small mono">{value}</span>
        </div>
      ))}
    </div>
  );
}

function OverviewTab({ d, onSaved }: { d: DeviceSummary; onSaved: () => void }) {
  const { push, fail } = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(d.name ?? '');
  const [terminalNumber, setTerminalNumber] = useState(d.terminalNumber ?? '');
  const [rolloutGroup, setRolloutGroup] = useState(d.rolloutGroup);

  const assign = useMutation({
    mutationFn: () =>
      api.patch(`/api/devices/${d.deviceId}/assignment`, { name, terminalNumber, rolloutGroup }),
    onSuccess: () => {
      push('Assignment updated');
      setEditing(false);
      onSaved();
    },
    onError: fail,
  });

  return (
    <div className="stack">
      <FieldGrid
        rows={[
          ['Device code', d.deviceCode],
          ['Device type', d.deviceType],
          ['Android version', d.androidVersion ?? '—'],
          ['App version', d.appVersion ?? '—'],
          ['Kiosk active', d.kioskActive === null ? '—' : d.kioskActive ? 'Yes' : 'No'],
          ['Rollout group', d.rolloutGroup],
        ]}
      />
      {editing ? (
        <div className="stack">
          <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <TextInput
            label="Terminal number"
            value={terminalNumber}
            onChange={(e) => setTerminalNumber(e.target.value)}
          />
          <Select
            label="Rollout group"
            value={rolloutGroup}
            onChange={(e) => setRolloutGroup(e.target.value as 'PILOT' | 'PRODUCTION')}
          >
            <option value="PRODUCTION">Production</option>
            <option value="PILOT">Pilot</option>
          </Select>
          <div className="row gap-6">
            <button className="btn primary sm" onClick={() => assign.mutate()} disabled={assign.isPending}>
              Save
            </button>
            <button className="btn sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="btn sm" onClick={() => setEditing(true)}>
          Edit assignment
        </button>
      )}
    </div>
  );
}

function ConfigurationTab({ deviceId, appliedVersion }: { deviceId: string; appliedVersion: number }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const versions = useQuery({
    queryKey: ['device-config-versions', deviceId],
    queryFn: () =>
      api.get<Array<{ version: number; config: Record<string, unknown>; note: string | null; createdAt: string }>>(
        `/api/devices/${deviceId}/config-versions`,
      ),
  });

  const rollback = useMutation({
    mutationFn: (version: number) => api.post(`/api/devices/${deviceId}/config/rollback`, { version }),
    onSuccess: () => {
      push('Rolled back to a new version matching that configuration');
      void queryClient.invalidateQueries({ queryKey: ['device-config-versions', deviceId] });
    },
    onError: fail,
  });

  if (versions.isLoading) return <Loading />;
  if (versions.error) return <ErrorState error={versions.error} onRetry={() => void versions.refetch()} />;
  if ((versions.data?.length ?? 0) === 0) {
    return <Empty message="No configuration has been published to this device yet" />;
  }

  return (
    <div className="table-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Version</th>
            <th>Applied</th>
            <th>Published</th>
            <th>Note</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {versions.data?.map((v) => (
            <tr key={v.version}>
              <td>v{v.version}</td>
              <td>{v.version === appliedVersion ? <Chip label="Applied" tone="ok" /> : '—'}</td>
              <td className="small">{dateTime(v.createdAt)}</td>
              <td className="small">{v.note ?? '—'}</td>
              <td>
                {v.version !== appliedVersion ? (
                  <button className="btn xs" onClick={() => rollback.mutate(v.version)}>
                    Roll back to this
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HealthTab({ d }: { d: DeviceSummary }) {
  const errors = useQuery({
    queryKey: ['device-errors', d.deviceId],
    queryFn: () =>
      api.get<Array<{ id: string; code: string; module: string; severity: string; message: string; occurredAt: string }>>(
        `/api/devices/${d.deviceId}/errors`,
      ),
  });

  return (
    <div className="stack">
      <FieldGrid
        rows={[
          ['Last heartbeat', d.lastHeartbeatAt ? dateTime(d.lastHeartbeatAt) : 'Never'],
          ['Last sync', d.lastSyncAt ? dateTime(d.lastSyncAt) : 'Never'],
          ['Pending sync operations', String(d.pendingSyncCount ?? '—')],
          ['Failed sync operations', String(d.failedSyncCount ?? '—')],
          ['Last sync error', d.lastSyncError ?? '—'],
          [
            'Battery',
            d.batteryPercent === null ? '—' : `${d.batteryPercent}%${d.batteryCharging ? ' (charging)' : ''}`,
          ],
          [
            'Storage available',
            d.storageAvailableMb === null
              ? '—'
              : `${d.storageAvailableMb} MB${d.storageTotalMb ? ` of ${d.storageTotalMb} MB` : ''}`,
          ],
          ['SQLite schema version', String(d.dbSchemaVersion ?? '—')],
        ]}
      />

      <div className="card-head">
        <h3>Recent errors</h3>
      </div>
      {errors.isLoading ? (
        <Loading />
      ) : (errors.data?.length ?? 0) === 0 ? (
        <Empty message="No errors reported" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Module</th>
                <th>Severity</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {errors.data?.map((e) => (
                <tr key={e.id}>
                  <td className="small">{dateTime(e.occurredAt)}</td>
                  <td className="small">{e.module}</td>
                  <td>
                    <Chip
                      label={e.severity}
                      tone={e.severity === 'CRITICAL' || e.severity === 'ERROR' ? 'danger' : 'warn'}
                    />
                  </td>
                  <td className="small">{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CommandsTab({ deviceId }: { deviceId: string }) {
  const commands = useQuery({
    queryKey: ['device-commands', deviceId],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          type: string;
          status: string;
          createdAt: string;
          expiresAt: string;
          completedAt: string | null;
        }>
      >(`/api/devices/${deviceId}/commands`),
  });

  if (commands.isLoading) return <Loading />;
  if ((commands.data?.length ?? 0) === 0) {
    return <Empty message="No commands have been issued to this device" />;
  }

  return (
    <div className="table-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Type</th>
            <th>Status</th>
            <th>Issued</th>
            <th>Expires</th>
            <th>Completed</th>
          </tr>
        </thead>
        <tbody>
          {commands.data?.map((c) => (
            <tr key={c.id}>
              <td className="small mono">{c.type}</td>
              <td>
                <Chip
                  label={c.status}
                  tone={c.status === 'COMPLETED' ? 'ok' : c.status === 'FAILED' || c.status === 'EXPIRED' ? 'danger' : ''}
                />
              </td>
              <td className="small">{dateTime(c.createdAt)}</td>
              <td className="small">{dateTime(c.expiresAt)}</td>
              <td className="small">{c.completedAt ? dateTime(c.completedAt) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UpdatesTab({ deviceId }: { deviceId: string }) {
  const status = useQuery({
    queryKey: ['device-update-status', deviceId],
    queryFn: () =>
      api.get<{
        currentVersion: string | null;
        latestVersion: string | null;
        minimumSupportedVersion: string | null;
        readiness: 'UP_TO_DATE' | 'UPDATE_AVAILABLE' | 'UPDATE_REQUIRED';
        releaseNotes: string | null;
      }>(`/api/devices/${deviceId}/update-status`),
  });
  const history = useQuery({
    queryKey: ['device-update-history', deviceId],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          previousVersion: string;
          targetVersion: string;
          status: string;
          startedAt: string | null;
          completedAt: string | null;
          failedAt: string | null;
          errorMessage: string | null;
        }>
      >(`/api/devices/${deviceId}/update-history`),
  });

  return (
    <div className="stack">
      {status.isLoading ? (
        <Loading />
      ) : status.data ? (
        <FieldGrid
          rows={[
            ['Current version', status.data.currentVersion ?? '—'],
            ['Latest eligible version', status.data.latestVersion ?? '—'],
            ['Minimum supported version', status.data.minimumSupportedVersion ?? '—'],
            ['Readiness', status.data.readiness],
          ]}
        />
      ) : null}

      <div className="card-head">
        <h3>Update history</h3>
      </div>
      {history.isLoading ? (
        <Loading />
      ) : (history.data?.length ?? 0) === 0 ? (
        <Empty message="No update has been attempted on this device" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Status</th>
                <th>Started</th>
                <th>Finished</th>
              </tr>
            </thead>
            <tbody>
              {history.data?.map((h) => (
                <tr key={h.id}>
                  <td className="small mono">{h.previousVersion}</td>
                  <td className="small mono">{h.targetVersion}</td>
                  <td>
                    <Chip label={h.status} tone={h.status === 'COMPLETED' ? 'ok' : h.status === 'FAILED' ? 'danger' : ''} />
                  </td>
                  <td className="small">{h.startedAt ? dateTime(h.startedAt) : '—'}</td>
                  <td className="small">
                    {h.completedAt ? dateTime(h.completedAt) : h.failedAt ? dateTime(h.failedAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuditTab({ deviceId }: { deviceId: string }) {
  const audit = useQuery({
    queryKey: ['device-audit', deviceId],
    queryFn: () =>
      api.get<Array<{ id: string; action: string; summary: string; actor: string; at: string }>>(
        `/api/devices/${deviceId}/audit`,
      ),
  });

  if (audit.isLoading) return <Loading />;
  if ((audit.data?.length ?? 0) === 0) return <Empty message="No audited actions for this device yet" />;

  return (
    <div className="table-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>What</th>
          </tr>
        </thead>
        <tbody>
          {audit.data?.map((a) => (
            <tr key={a.id}>
              <td className="small">{dateTime(a.at)}</td>
              <td className="small">{a.actor}</td>
              <td className="small">{a.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
